// Manager Allocation - the SOLE write path for employee.reporting_manager.
// Governed by the access-level hierarchy enforced server-side at
// PATCH /api/employee/assign-manager/:empId. The Employee Center modal and
// the Excel bulk import do not touch this field.
import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "./utils/api";
import logo_eagle from "./assets/img/logo_eagle.png";
import { profileColorFromName, initialsFromName } from "./utils/profileColor";
import { useDsprAlerts } from "./utils/dSprAlerts";
import "./Manager_Allocation.css";

// Access-level hierarchy mirror of the backend rule (ACCESS_LEVEL_RANK in
// Employee_server.js). Used to classify rows client-side.
const ACCESS_RANK = { "Admin": 4, "Sub Admin": 3, "Manager": 2, "User": 1 };

// Format the joined manager fields as "First Last (DZIND###)". Mirrors the
// helper in Employee_Center.js so the two pages display identically.
const formatManager = (emp) => {
    if (!emp || !emp.reporting_manager) return null;
    const first = emp.reporting_manager_first_name || "";
    const last = emp.reporting_manager_last_name || "";
    const fullName = `${first} ${last}`.trim();
    return fullName ? `${fullName} (${emp.reporting_manager})` : emp.reporting_manager;
};

// Classify each employee row into one of four states.
//   topTier   - emp_access_level is Admin (manager is intentionally NULL)
//   unassigned - reporting_manager IS NULL and employee is NOT Admin
//   invalid   - reporting_manager IS set but the joined manager is now
//               inactive / deleted / no longer higher-tier than this row
//   assigned  - everything else (valid, current, in-hierarchy)
function classify(emp) {
    if (emp.emp_access_level === "Admin") return "topTier";
    if (!emp.reporting_manager) return "unassigned";
    // Joined columns will be NULL if manager was soft-deleted (the LEFT JOIN
    // includes `m.deleted_time IS NULL`, so a deleted manager won't return rows).
    if (emp.reporting_manager_first_name === null && emp.reporting_manager_last_name === null) {
        return "invalid"; // manager soft-deleted since assignment
    }
    if (emp.reporting_manager_active === 0) return "invalid"; // manager went inactive
    const mgrRank = ACCESS_RANK[emp.reporting_manager_access_level] || 0;
    const empRank = ACCESS_RANK[emp.emp_access_level] || 0;
    if (mgrRank <= empRank) return "invalid"; // hierarchy mismatch (e.g. manager demoted)
    return "assigned";
}

function invalidReason(emp) {
    if (emp.reporting_manager_first_name === null && emp.reporting_manager_last_name === null) {
        return "Manager deleted";
    }
    if (emp.reporting_manager_active === 0) return "Manager inactive";
    const mgrRank = ACCESS_RANK[emp.reporting_manager_access_level] || 0;
    const empRank = ACCESS_RANK[emp.emp_access_level] || 0;
    if (mgrRank <= empRank) return "Hierarchy mismatch";
    return "Unknown";
}

const SELECT_STYLES = {
    container: (p) => ({ ...p, minWidth: "240px" }),
    control: (p, s) => ({
        ...p,
        borderRadius: 10,
        minHeight: 38,
        borderColor: s.isFocused ? "#E8520A" : "#E2E8F0",
        boxShadow: s.isFocused ? "0 0 0 3px rgba(232, 82, 10, 0.12)" : "none",
        "&:hover": { borderColor: s.isFocused ? "#E8520A" : "#CBD5E1" },
    }),
    option: (p, s) => ({
        ...p,
        backgroundColor: s.isSelected ? "#E8520A" : s.isFocused ? "#FFF5F0" : "white",
        color: s.isSelected ? "white" : "#0F172A",
        padding: "9px 12px",
        fontSize: 13,
    }),
    placeholder: (p) => ({ ...p, color: "#94A3B8", fontSize: 13 }),
    singleValue: (p) => ({ ...p, color: "#0F172A", fontSize: 13 }),
};

// Per-access-level pill colors. Mirrors the hierarchy at a glance -
// Admin = navy, Sub Admin = indigo, Manager = orange, User = slate.
const ACCESS_PILL = {
    "Admin":     { bg: "#0F172A", fg: "#FFFFFF" },
    "Sub Admin": { bg: "#EEF2FF", fg: "#4338CA" },
    "Manager":   { bg: "#FFF5F0", fg: "#E8520A" },
    "User":      { bg: "#F1F5F9", fg: "#475569" },
};

function ManagerAllocation({ navSize }) {
    const navigate = useNavigate();
    const { showAlert, AlertsHost } = useDsprAlerts();
    const [employees, setEmployees] = useState([]);
    const [tab, setTab] = useState("all"); // all | unassigned | invalid | topTier
    const [search, setSearch] = useState("");
    const [deptFilter, setDeptFilter] = useState(null);
    const [accessFilter, setAccessFilter] = useState(null);
    const [eligibleByLevel, setEligibleByLevel] = useState({}); // cache: { "User": [...], "Manager": [...], ... }
    const [editingEmpId, setEditingEmpId] = useState(null);
    const [editingPick, setEditingPick] = useState(null); // selected option in single-edit mode
    const [bulkMode, setBulkMode] = useState(false);
    const [bulkChanges, setBulkChanges] = useState(new Map()); // empId -> managerEmpId or null
    const [busy, setBusy] = useState(null); // null or label string for overlay

    // ── Initial load: confirm user has access + fetch employee list ──────────
    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch("/api/employee/logined_employee", { credentials: "include" });
                const data = await res.json();
                if (data?.message === "Access Denied. No Token Provided!" || data?.message === "Invalid Token") {
                    navigate("/login"); return;
                }
                const me = Array.isArray(data) ? data[0] : data;
                if (!me?.emp_id) { navigate("/login"); return; }
                // Role redirect removed - ProtectedRoute (App.js) gates via matrix.
            } catch (e) {
                navigate("/login"); return;
            }
            await fetchEmployees();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchEmployees = async () => {
        try {
            const res = await apiFetch("/api/employee/all", { credentials: "include" });
            const json = await res.json();
            setEmployees(Array.isArray(json) ? json.filter(e => !e.deleted_time) : []);
        } catch (e) {
            console.error("fetchEmployees:", e);
            showAlert("error", "Failed to load employees", e.message);
        }
    };

    // Lazy-fetch the eligible-managers list for a given access level (cached).
    const getEligible = async (accessLevel) => {
        if (eligibleByLevel[accessLevel]) return eligibleByLevel[accessLevel];
        try {
            const res = await apiFetch(`/api/employee/eligible-managers/${encodeURIComponent(accessLevel)}`, { credentials: "include" });
            const list = await res.json();
            setEligibleByLevel(prev => ({ ...prev, [accessLevel]: list }));
            return list;
        } catch (e) {
            console.error("getEligible:", e);
            return [];
        }
    };

    // ── Derived: stats counts, filter options ───────────────────────────────
    const stats = useMemo(() => {
        const acc = { assigned: 0, unassigned: 0, invalid: 0, topTier: 0 };
        employees.forEach(emp => { acc[classify(emp)]++; });
        return acc;
    }, [employees]);

    const departmentOptions = useMemo(() => {
        const set = new Set(employees.map(e => e.emp_department).filter(Boolean));
        return [...set].sort().map(d => ({ value: d, label: d }));
    }, [employees]);

    const accessLevelOptions = useMemo(() =>
        ["Admin", "Sub Admin", "Manager", "User"].map(a => ({ value: a, label: a })),
        []);

    // ── Filtering pipeline: tab → dept → access-level → search ──────────────
    const visibleRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        return employees
            .filter(emp => {
                if (tab === "all") return true;
                return classify(emp) === tab;
            })
            .filter(emp => !deptFilter || emp.emp_department === deptFilter.value)
            .filter(emp => !accessFilter || emp.emp_access_level === accessFilter.value)
            .filter(emp => {
                if (!q) return true;
                const blob = [emp.emp_id, emp.emp_first_name, emp.emp_last_name,
                emp.emp_mail_id, emp.reporting_manager,
                emp.reporting_manager_first_name, emp.reporting_manager_last_name]
                    .filter(Boolean).join(" ").toLowerCase();
                return blob.includes(q);
            });
    }, [employees, tab, deptFilter, accessFilter, search]);

    // ── Edit handlers ────────────────────────────────────────────────────────
    const startEdit = async (emp) => {
        await getEligible(emp.emp_access_level); // warm cache
        setEditingEmpId(emp.emp_id);
        setEditingPick(emp.reporting_manager
            ? { value: emp.reporting_manager, label: formatManager(emp) }
            : null);
    };

    const cancelEdit = () => { setEditingEmpId(null); setEditingPick(null); };

    const saveSingle = async (emp) => {
        const managerEmpId = editingPick ? editingPick.value : null;
        setBusy("Saving…");
        try {
            const res = await apiFetch(`/api/employee/assign-manager/${emp.emp_id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ managerEmpId }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Save failed");
            // Patch the local row in-place so the page refreshes without reload
            setEmployees(prev => prev.map(e => e.emp_id === emp.emp_id ? data.employee : e));
            cancelEdit();
            showAlert("success", "Updated");
        } catch (e) {
            showAlert("error", "Could not save", e.message);
        } finally {
            setBusy(null);
        }
    };

    // ── Bulk Mode handlers ───────────────────────────────────────────────────
    const stageBulkChange = (empId, option) => {
        setBulkChanges(prev => {
            const next = new Map(prev);
            next.set(empId, option ? option.value : null);
            return next;
        });
    };

    const saveBulk = async () => {
        if (bulkChanges.size === 0) {
            showAlert("info", "Nothing to save", "No rows have been changed.");
            return;
        }
        setBusy(`Saving ${bulkChanges.size} change${bulkChanges.size !== 1 ? "s" : ""}…`);
        const results = await Promise.all([...bulkChanges.entries()].map(async ([empId, managerEmpId]) => {
            try {
                const res = await apiFetch(`/api/employee/assign-manager/${empId}`, {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({ managerEmpId }),
                });
                const data = await res.json();
                if (!res.ok) return { empId, ok: false, error: data.error || "Save failed", employee: null };
                return { empId, ok: true, employee: data.employee };
            } catch (e) {
                return { empId, ok: false, error: e.message, employee: null };
            }
        }));
        // Apply successful updates inline; refetch on partial failure for safety
        const okMap = new Map(results.filter(r => r.ok).map(r => [r.empId, r.employee]));
        setEmployees(prev => prev.map(e => okMap.has(e.emp_id) ? okMap.get(e.emp_id) : e));
        setBulkChanges(new Map());
        setBusy(null);

        const successCount = results.filter(r => r.ok).length;
        const failures = results.filter(r => !r.ok);
        if (failures.length === 0) {
            showAlert("success", `All ${successCount} updated`);
        } else {
            const summary = failures.length <= 3
                ? failures.map(f => `${f.empId}: ${f.error}`).join(" · ")
                : `${failures.slice(0, 3).map(f => f.empId).join(", ")} (+${failures.length - 3} more)`;
            showAlert(
                failures.length === results.length ? "error" : "warning",
                `${successCount} saved, ${failures.length} failed`,
                summary,
            );
        }
    };

    // ── Render helpers ──────────────────────────────────────────────────────
    const renderManagerInfo = (emp) => {
        if (emp.emp_access_level === "Admin") {
            return <span className="ma-pill ma-pill-neutral">Top-tier · no manager</span>;
        }
        const cls = classify(emp);
        if (cls === "unassigned") return <span className="ma-pill ma-pill-red">⚠ Unassigned</span>;
        if (cls === "invalid") {
            return (
                <div className="ma-mgr-invalid">
                    <span className="ma-pill ma-pill-amber">⚠ {invalidReason(emp)}</span>
                    <span className="ma-current-stale">was: {emp.reporting_manager}</span>
                </div>
            );
        }
        return <span className="ma-manager-chip">{formatManager(emp)}</span>;
    };

    const renderAction = (emp) => {
        if (emp.emp_access_level === "Admin") return <span className="ma-muted">-</span>;
        const eligible = eligibleByLevel[emp.emp_access_level] || [];
        if (bulkMode) {
            const staged = bulkChanges.has(emp.emp_id) ? bulkChanges.get(emp.emp_id) : emp.reporting_manager;
            const value = staged ? (eligible.find(o => o.value === staged) || { value: staged, label: formatManager(emp) || staged }) : null;
            return (
                <Select
                    options={eligible}
                    value={value}
                    isClearable
                    placeholder="Pick manager…"
                    onMenuOpen={() => { if (!eligibleByLevel[emp.emp_access_level]) getEligible(emp.emp_access_level); }}
                    onChange={(opt) => stageBulkChange(emp.emp_id, opt)}
                    styles={SELECT_STYLES}
                />
            );
        }
        if (editingEmpId === emp.emp_id) {
            return (
                <div className="ma-edit-row">
                    <Select
                        options={eligible}
                        value={editingPick}
                        isClearable
                        placeholder="Pick manager…"
                        onChange={setEditingPick}
                        styles={SELECT_STYLES}
                    />
                    <button className="ma-btn ma-btn-save" onClick={() => saveSingle(emp)} disabled={!!busy}>Save</button>
                    <button className="ma-btn ma-btn-cancel" onClick={cancelEdit} disabled={!!busy}>Cancel</button>
                </div>
            );
        }
        return (
            <button className="ma-btn ma-btn-edit" onClick={() => startEdit(emp)}>
                {emp.reporting_manager ? "Change" : "Assign"}
            </button>
        );
    };

    // First N affected names for the leakage banner
    const affectedNames = useMemo(() =>
        employees
            .filter(e => {
                const c = classify(e);
                return c === "unassigned" || c === "invalid";
            })
            .slice(0, 8)
            .map(e => `${e.emp_first_name} ${e.emp_last_name}`),
        [employees]);

    const totalGaps = stats.unassigned + stats.invalid;

    return (
        <div className="ma-container">
            {busy && (
                <div className="ec-loader-overlay">
                    <div className="ec-loader-spinner" />
                    <div className="ec-loader-text">{busy}</div>
                </div>
            )}

            <div className={`ma-dashboard ${navSize}`}>
                {/* Mounted INSIDE the dashboard so the banner stays in the
                    dashboard's block layout instead of becoming a flex-row
                    sibling of it (which caused a visible horizontal jump). */}
                <AlertsHost />
                {/* ── Page header - clean, no white card around it (matches dSpr) ── */}
                <div className="ma-header-wrapper">
                    <div className="ma-header">
                        <h2>
                            <a className="navbar-brand" href="#"><img src={logo_eagle} alt="dAdmin Logo" style={{ height: 50, objectFit: "contain", marginLeft: -20 }} /></a>
                            Manager Allocation
                        </h2>
                        <p className="ma-subtitle">
                            Assign each employee's reporting manager. Hierarchy enforced server-side - managers must be strictly higher access level.
                        </p>
                    </div>
                </div>

                <div className="ma-dashboard-content">

                {/* ── Stat tiles - vibrant tinted cards. Click to filter. ── */}
                <div className="ma-stats">
                    {[
                        { tabKey: "assigned",   variant: "green",   num: stats.assigned,   label: "Assigned",   hint: "Valid hierarchy" },
                        { tabKey: "unassigned", variant: "red",     num: stats.unassigned, label: "Unassigned", hint: "Needs a manager" },
                        { tabKey: "invalid",    variant: "amber",   num: stats.invalid,    label: "Invalid",    hint: "Manager demoted / inactive / deleted" },
                        { tabKey: "topTier",    variant: "slate",   num: stats.topTier,    label: "Top-tier",   hint: "Admin (no manager required)" },
                    ].map(card => {
                        const isActive = tab === card.tabKey;
                        return (
                            <div key={card.tabKey}
                                className={`ma-stat ma-stat-${card.variant} ${isActive ? "active" : ""}`}
                                role="button"
                                tabIndex={0}
                                onClick={() => setTab(isActive ? "all" : card.tabKey)}
                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setTab(isActive ? "all" : card.tabKey); } }}>
                                <div className="ma-stat-num">{card.num}</div>
                                <div className="ma-stat-label">{card.label}</div>
                                <div className="ma-stat-hint">{card.hint}</div>
                                <div className="ma-stat-cta">
                                    {isActive ? "✓ Filter active - click to clear" : "Click to filter"}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* ── Hierarchy Gaps banner ── */}
                {totalGaps > 0 && (
                    <div className="ma-leakage">
                        <div className="ma-leakage-icon">⚠</div>
                        <div className="ma-leakage-text">
                            <strong>Reporting Hierarchy Gaps - {totalGaps} employee{totalGaps !== 1 ? "s" : ""} need attention</strong>
                            <div className="ma-leakage-names">
                                {affectedNames.length > 0 ? affectedNames.join("  ·  ") : ""}
                                {employees.filter(e => { const c = classify(e); return c === "unassigned" || c === "invalid"; }).length > affectedNames.length ? " …" : ""}
                            </div>
                        </div>
                        <button type="button"
                            className="ma-leakage-cta"
                            onClick={() => setTab(stats.unassigned >= stats.invalid ? "unassigned" : "invalid")}>
                            Resolve →
                        </button>
                    </div>
                )}

                {/* ── Filter tab row + Bulk Mode toggle ── */}
                <div className="ma-toolbar">
                    <div className="ma-tabs">
                        {[
                            { key: "all",        label: "All employees", count: employees.length },
                            { key: "assigned",   label: "Assigned",      count: stats.assigned },
                            { key: "unassigned", label: "Unassigned",    count: stats.unassigned },
                            { key: "invalid",    label: "Invalid",       count: stats.invalid },
                            { key: "topTier",    label: "Top-tier",      count: stats.topTier },
                        ].map(t => (
                            <button key={t.key}
                                className={`ma-tab ${tab === t.key ? "active" : ""}`}
                                onClick={() => setTab(t.key)}>
                                {t.label}
                                <span className="ma-tab-count">{t.count}</span>
                            </button>
                        ))}
                    </div>
                    {/* Bulk Mode toggle - proper slider, not a stock checkbox */}
                    <label className={`ma-toggle ${bulkMode ? "on" : ""}`}>
                        <input type="checkbox" checked={bulkMode} onChange={(e) => {
                            setBulkMode(e.target.checked);
                            cancelEdit();
                            if (!e.target.checked) setBulkChanges(new Map());
                        }} />
                        <span className="ma-toggle-track"><span className="ma-toggle-knob" /></span>
                        <span className="ma-toggle-label">Bulk Mode</span>
                    </label>
                </div>

                {/* ── Search + filter selects ── */}
                <div className="ma-filters">
                    <div className="ma-search-wrap">
                        <span className="ma-search-icon" aria-hidden="true">🔍</span>
                        <input type="text" className="ma-search"
                            placeholder="Search name, emp ID, email, current manager…"
                            value={search} onChange={(e) => setSearch(e.target.value)} />
                        {search && (
                            <button type="button" className="ma-search-clear"
                                onClick={() => setSearch("")} aria-label="Clear search">×</button>
                        )}
                    </div>
                    <Select className="ma-select-wrap" options={departmentOptions} value={deptFilter}
                        onChange={setDeptFilter} isClearable placeholder="All departments" styles={SELECT_STYLES} />
                    <Select className="ma-select-wrap" options={accessLevelOptions} value={accessFilter}
                        onChange={setAccessFilter} isClearable placeholder="All access levels" styles={SELECT_STYLES} />
                </div>

                {/* ── Employee list - card-style rows (no more cramped table) ── */}
                <div className="ma-list">
                    {visibleRows.length === 0 ? (
                        <div className="ma-empty">
                            <div className="ma-empty-icon">📭</div>
                            <div className="ma-empty-title">No employees match the current filters.</div>
                            <div className="ma-empty-hint">Try a different tab or clear the search.</div>
                        </div>
                    ) : visibleRows.map(emp => {
                        const cls = classify(emp);
                        const fullName = `${emp.emp_first_name || ""} ${emp.emp_last_name || ""}`.trim();
                        const initials = initialsFromName(emp.emp_first_name, emp.emp_last_name);
                        const access = ACCESS_PILL[emp.emp_access_level] || ACCESS_PILL.User;
                        return (
                            <div key={emp.emp_id} className={`ma-row ma-row-${cls}`}>
                                <div className="ma-row-identity">
                                    <div className="ma-row-avatar"
                                        style={{ background: profileColorFromName(emp.emp_first_name), color: "#fff" }}>
                                        {initials}
                                    </div>
                                    <div className="ma-row-meta">
                                        <div className="ma-row-name-line">
                                            <strong>{fullName || emp.emp_id}</strong>
                                            <span className="ma-row-id">{emp.emp_id}</span>
                                            <span className="ma-row-access"
                                                style={{ background: access.bg, color: access.fg }}>
                                                {emp.emp_access_level || "-"}
                                            </span>
                                        </div>
                                        <div className="ma-row-sub">
                                            {emp.job_position || "-"}
                                            <span className="ma-sep">·</span>
                                            {emp.emp_department || "-"}
                                        </div>
                                    </div>
                                </div>
                                <div className="ma-row-manager">
                                    <div className="ma-row-mgr-label">CURRENT MANAGER</div>
                                    {renderManagerInfo(emp)}
                                </div>
                                <div className="ma-row-action">
                                    {bulkMode && emp.emp_access_level !== "Admin" && (
                                        <div className="ma-row-mgr-label">NEW MANAGER</div>
                                    )}
                                    {renderAction(emp)}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* ── Bulk save bar - sticky at bottom while bulk mode is on ── */}
                {bulkMode && (
                    <div className="ma-bulk-bar">
                        <div className="ma-bulk-bar-info">
                            <span className="ma-bulk-bar-icon">📝</span>
                            <span>
                                <strong>{bulkChanges.size}</strong> pending change{bulkChanges.size !== 1 ? "s" : ""}
                                {bulkChanges.size > 0 && " - review before saving"}
                            </span>
                        </div>
                        <button className="ma-btn ma-btn-clear" disabled={!!busy || bulkChanges.size === 0}
                            onClick={() => setBulkChanges(new Map())}>Discard</button>
                        <button className="ma-btn ma-btn-save-all" disabled={!!busy || bulkChanges.size === 0}
                            onClick={saveBulk}>Save all changes</button>
                    </div>
                )}
                </div>{/* /.ma-dashboard-content */}
            </div>
        </div>
    );
}

export default ManagerAllocation;
